import React, { useState } from 'react';
import { Facebook, Instagram, Linkedin, Youtube, X } from 'lucide-react';

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Generate array of all event images
  const allEventImages = Array.from({ length: 49 }, (_, i) => ({
    id: i + 1,
    src: `/images/events/event_${String(i + 1).padStart(2, '0')}.jpg`,
    alt: `Care Pharmacy Event ${i + 1}`
  }));

  // Store images (existing)
  const storeImages = [
    { 
      image: '/images/care_pharmacy_1.jpeg', 
      title: 'Modern Store Interiors', 
      description: 'Well-organized shelves with 35,000+ products and professional checkout systems' 
    },
    { 
      image: '/images/care_pharmacy_2.jpeg', 
      title: 'Premium Store Layout', 
      description: 'Spacious aisles and comprehensive product range for easy shopping experience' 
    },
    { 
      image: '/images/care_pharmacy_3.jpeg', 
      title: 'Quality Product Display', 
      description: 'Clean and accessible product arrangements with clear pricing and information' 
    },
    { 
      image: '/images/care_pharmacy_4.jpeg', 
      title: 'Customer Service Area', 
      description: 'Dedicated consultation spaces with professional pharmacists for guidance' 
    },
    { 
      image: '/images/care_pharmacy_6.jpeg', 
      title: 'Comprehensive Inventory', 
      description: 'Wide selection of medicines, wellness products, and healthcare essentials' 
    },
    { 
      image: '/images/care_pharmacy_5.jpeg', 
      title: 'Care Pharmacy Brand', 
      description: 'Established presence across 190+ locations serving millions of customers' 
    }
  ];

  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              In Pictures
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              Explore our stores, launches and community work. Follow us on social media for regular updates.
            </p>
          </div>
        </div>
      </section>

      {/* Events & Moments Gallery */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Events & Moments
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            Store openings, team celebrations, community health camps, and memorable moments from our 190+ locations across India.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {allEventImages.map((img) => (
              <div
                key={img.id}
                className="hover-lift"
                onClick={() => openLightbox(img.src)}
                style={{
                  overflow: 'hidden',
                  borderRadius: '0px',
                  cursor: 'pointer',
                  border: '1px solid var(--border-light)',
                  background: 'var(--bg-primary)'
                }}
              >
                <img 
                  src={img.src} 
                  alt={img.alt}
                  style={{
                    width: '100%',
                    height: '240px',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Store Gallery */}
      <section className="section-padding-small" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Store Interiors
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px'
          }}>
            {storeImages.map((item, index) => (
              <div key={index} className="hover-lift" style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                overflow: 'hidden',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{
                  height: '280px',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer'
                }}
                onClick={() => openLightbox(item.image)}
                >
                  <img 
                    src={item.image} 
                    alt={item.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </div>
                <div style={{ padding: '24px' }}>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                    {item.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px'
          }}
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
          >
            <X size={24} />
          </button>
          <img
            src={selectedImage}
            alt="Full size"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Social Media Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Follow Us on Social Media
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '48px',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 48px'
          }}>
            Stay updated with our latest store openings, health tips, special offers, and community initiatives.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <a
              href="https://www.facebook.com/share/1GE4bJyfsa/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-lift"
              style={{
                padding: '40px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <Facebook size={48} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                Facebook
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Follow for store updates and health awareness
              </p>
            </a>

            <a
              href="https://www.instagram.com/brijeshpatel_pharmaretailchain?igsh=N2FsZ3V2NTY5ZjBj&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-lift"
              style={{
                padding: '40px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <Instagram size={48} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                Instagram
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Visual stories from our stores and team
              </p>
            </a>

            <a
              href="https://www.linkedin.com/in/brijeshpharmacist?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-lift"
              style={{
                padding: '40px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <Linkedin size={48} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                LinkedIn
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Professional updates and career opportunities
              </p>
            </a>

            <a
              href="https://youtube.com/@brijeshpatel_pharmaretail?si=mDMfwR1mHsxbreKl"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-lift"
              style={{
                padding: '40px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <Youtube size={48} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                YouTube
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Health tips, store tours, and educational content
              </p>
            </a>

            <a
              href="https://www.threads.com/@brijeshpatel_pharmaretailchain?invite=0"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-lift"
              style={{
                padding: '40px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 16px',
                background: 'var(--brand-blue)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: '600',
                color: 'white'
              }}>
                @
              </div>
              <h3 className="heading-3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                Threads
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Join our community conversations
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Community Impact
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '40px'
          }}>
            {[
              { title: 'Health Camps', description: 'Free health check-ups and awareness programs in underserved communities.' },
              { title: 'Medicine Donation', description: 'Supporting NGOs and charitable hospitals with essential medicines.' },
              { title: 'Health Education', description: 'Workshops on diabetes care, hypertension management, and preventive health.' },
              { title: 'Emergency Support', description: 'Quick response during natural disasters and medical emergencies.' }
            ].map((item, index) => (
              <div key={index} style={{
                padding: '32px',
                background: 'var(--bg-primary)',
                borderRadius: '0px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;